'use client';

import { useState } from 'react';
import Link from 'next/link';
import {
  ArrowRight, Check, MessageSquare, Layers, Shield, Phone, Mail, FileText,
  Briefcase, Users, Building2, ChevronRight, Download, Gauge, Database, Activity,
} from 'lucide-react';
import { APP_VERSION } from '@/lib/version';

const ONE_PAGER_URL = `/ser-ai-insight-one-pager.pdf?v=${encodeURIComponent(APP_VERSION)}`;

// ─── Modelos de contratação (sem preços, sem planos fechados) ────────────────
const TRACKS = [
  {
    id: 'explorar',
    icon: MessageSquare,
    accent: 'text-blue-400',
    border: 'border-blue-500/30 bg-blue-500/5',
    name: 'Conhecer a plataforma',
    short: 'Demo guiada e exploração',
    description:
      'Quer entender como funciona o Painel Executivo, as Mesas, o Squad de Analistas e o que conecta nas suas fontes — antes de tomar qualquer decisão.',
    fits: [
      'Heads avaliando ferramentas de inteligência',
      'Times querendo entender Squad Seraik',
      'Decisores que pedem demo aplicada',
    ],
  },
  {
    id: 'operacao',
    icon: Layers,
    accent: 'text-violet-400',
    border: 'border-violet-500/30 bg-violet-500/5',
    name: 'Operação para o time',
    short: 'Setup completo, multi-workspace',
    description:
      'Sua equipe vai operar todo dia: conectar fontes, montar mesas por cliente, ativar Painel Executivo, alertas e tarefas. Configuração desenhada com você.',
    fits: [
      'Heads de Growth e Performance',
      'Agências com vários clientes',
      'CMOs que querem operação 24/7',
    ],
  },
  {
    id: 'enterprise',
    icon: Shield,
    accent: 'text-cyan-400',
    border: 'border-cyan-500/30 bg-cyan-500/5',
    name: 'Customizado / On-Premise',
    short: 'Volume alto, regulação, deploy privado',
    description:
      'Empresas reguladas (BACEN, SUSEP, ANS, LGPD), volumes altos, integração com stack interna, SSO, deploy via Docker/Kubernetes no seu ambiente. Dados nunca saem da sua infra.',
    fits: [
      'Bancos, seguradoras, fintechs',
      'Saúde, governo, regulados',
      'Grandes empresas com SSO',
    ],
  },
];

// ─── Capacidades resumidas (referência rápida, sem virar tabela de planos) ───
const HIGHLIGHTS = [
  { icon: Gauge, label: 'Painel Executivo por segmento' },
  { icon: Layers, label: 'Mesas de Performance multi-fonte' },
  { icon: MessageSquare, label: 'Chat com Squad de Analistas' },
  { icon: Database, label: '12+ conectores oficiais' },
  { icon: Activity, label: 'Tarefas e alertas proativos' },
  { icon: Shield, label: 'Workspace isolation + RLS' },
];

interface ContactFormData {
  name: string;
  email: string;
  company: string;
  role: string;
  teamSize: string;
  message: string;
  trackInterest: string;
}

export default function PricingPage() {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    company: '',
    role: '',
    teamSize: '',
    message: '',
    trackInterest: 'explorar',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/enterprise-lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, planInterest: formData.trackInterest }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Erro ao enviar');
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar formulário. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border/40 px-6 py-4 flex items-center justify-between">
        <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
          ← Voltar
        </Link>
        <span className="text-xs text-muted-foreground">Seraik Insights · by aunica labs</span>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-14">
          <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Como contratar</p>
          <h1 className="text-4xl font-bold tracking-tight mb-4">
            Sem plano de prateleira.<br />
            <span className="text-blue-400">Setup desenhado com você.</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            O Seraik Insights não vende módulo avulso por preço fixo. A operação que faz sentido
            depende do tamanho do time, das fontes que você usa e do nível de automação que quer.
            Conta o que precisa — desenhamos o caminho certo, sem você comprar o que não vai usar.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <button
              onClick={() => { setShowForm(true); setFormData((f) => ({ ...f, trackInterest: 'explorar' })); }}
              className="rounded-lg bg-foreground text-background px-5 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            >
              Falar com especialista <ArrowRight className="h-4 w-4" />
            </button>
            <a
              href={ONE_PAGER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg border border-border/60 px-5 py-2.5 text-sm font-medium hover:bg-muted/30 transition-colors flex items-center justify-center gap-2"
            >
              <Download className="h-4 w-4" /> Baixar one-pager
            </a>
          </div>
        </div>

        {/* Trilhas de contratação */}
        <div className="mb-16">
          <p className="text-xs text-muted-foreground uppercase tracking-widest text-center mb-6">Por onde começar</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {TRACKS.map((track) => (
              <div
                key={track.id}
                className={`relative rounded-xl border p-6 flex flex-col ${track.border}`}
              >
                <div className={`h-10 w-10 rounded-lg bg-background/40 flex items-center justify-center mb-4`}>
                  <track.icon className={`h-5 w-5 ${track.accent}`} />
                </div>
                <h3 className="font-semibold text-base mb-1">{track.name}</h3>
                <p className={`text-xs uppercase tracking-widest mb-3 ${track.accent}`}>{track.short}</p>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">{track.description}</p>
                <div className="mb-5 flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Faz sentido se você é:</p>
                  <ul className="space-y-1.5">
                    {track.fits.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <Check className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  onClick={() => { setShowForm(true); setFormData((f) => ({ ...f, trackInterest: track.id })); }}
                  className="w-full rounded-lg bg-foreground text-background py-2.5 text-sm font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                >
                  Falar sobre essa trilha <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* O que está incluso (sempre) */}
        <div className="mb-16 rounded-2xl border border-border/40 bg-muted/10 p-8">
          <p className="text-xs text-muted-foreground uppercase tracking-widest text-center mb-6">O que está sempre incluso</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            {HIGHLIGHTS.map((h) => (
              <div key={h.label} className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <h.icon className="h-4 w-4 text-blue-400" />
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">{h.label}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">
            O escopo exato — número de workspaces, membros, conectores ativos, mensagens/mês,
            integrações customizadas — é dimensionado na conversa de setup.
          </p>
        </div>

        {/* Canais de contato */}
        <div className="mb-16">
          <p className="text-xs text-muted-foreground uppercase tracking-widest text-center mb-6">Como falar com a gente</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setShowForm(true)}
              className="rounded-xl border border-border/40 bg-muted/10 p-6 text-left hover:bg-muted/20 transition-colors group"
            >
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center mb-3">
                <FileText className="h-5 w-5 text-blue-400" />
              </div>
              <p className="font-semibold text-sm mb-1">Formulário</p>
              <p className="text-xs text-muted-foreground mb-3">Conta seu contexto e a gente volta com proposta dimensionada.</p>
              <span className="text-xs text-blue-400 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Preencher <ArrowRight className="h-3 w-3" />
              </span>
            </button>

            <a
              href="mailto:negocios@seraik.com"
              className="rounded-xl border border-border/40 bg-muted/10 p-6 hover:bg-muted/20 transition-colors group"
            >
              <div className="h-10 w-10 rounded-lg bg-violet-500/10 flex items-center justify-center mb-3">
                <Mail className="h-5 w-5 text-violet-400" />
              </div>
              <p className="font-semibold text-sm mb-1">E-mail</p>
              <p className="text-xs text-muted-foreground mb-3">Time comercial direto. Resposta em até 1 dia útil.</p>
              <span className="text-xs text-blue-400 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                negocios@seraik.com <ArrowRight className="h-3 w-3" />
              </span>
            </a>

            <a
              href="https://wa.me/5511963150786"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-xl border border-border/40 bg-muted/10 p-6 hover:bg-muted/20 transition-colors group"
            >
              <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center mb-3">
                <Phone className="h-5 w-5 text-green-400" />
              </div>
              <p className="font-semibold text-sm mb-1">WhatsApp</p>
              <p className="text-xs text-muted-foreground mb-3">+55 11 96315-0786 — conversa rápida, reunião agendada na hora.</p>
              <span className="text-xs text-blue-400 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Abrir conversa <ArrowRight className="h-3 w-3" />
              </span>
            </a>
          </div>
        </div>

        {/* FAQ comercial */}
        <div className="border-t border-border/40 pt-12">
          <h2 className="text-xl font-semibold mb-2 text-center">Perguntas frequentes sobre contratação</h2>
          <p className="text-sm text-muted-foreground text-center mb-10">As perguntas que aparecem antes de começar.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto text-sm">
            {[
              {
                q: 'Vocês têm preço de tabela?',
                a: 'Não. O escopo de operação varia muito (número de workspaces, fontes, mensagens, automações). Damos proposta dimensionada na primeira conversa, com base no que você realmente vai usar.',
              },
              {
                q: 'Posso começar pequeno e crescer depois?',
                a: 'Sim, é o caminho recomendado. O Starter já cobre 2 workspaces e 5 fontes — suficiente pra um cliente com 2 marcas/segmentos. Conforme valida, sobe pro Profissional/Growth com mais workspaces, membros, conectores e módulos.',
              },
              {
                q: 'Tem trial ou prova de conceito?',
                a: 'Sim. Para times maiores fazemos um POC com 1-2 workspaces durante 14 a 30 dias, com setup acompanhado pelo nosso time. Conta com a gente o que faz sentido testar.',
              },
              {
                q: 'Como funciona o On-Premise?',
                a: 'A plataforma é instalada no seu próprio ambiente (cloud privada ou data center). Dados nunca saem da sua infraestrutura — a aunica acompanha a implementação e o time interno passa a operar normalmente.',
              },
              {
                q: 'O que acontece se eu precisar de uma fonte que não está na lista?',
                a: 'Avaliamos caso a caso. A maioria das fontes de mercado é integrada em 2 a 4 semanas. Planilhas curadas ou arquivos enviados por seu sistema costumam ser ainda mais rápidos.',
              },
              {
                q: 'Vocês integram com a stack interna do meu time?',
                a: 'Sim. Tasks/Alertas via Slack ou Teams, exportação de relatórios, webhooks de eventos, SSO via SAML ou Google Workspace. Mapeamos as integrações necessárias na conversa de setup.',
              },
              {
                q: 'Qual o tempo médio de onboarding?',
                a: 'Setup técnico em 1-2 dias úteis (conexões + workspace + membros). Operação plena com Painel + Mesas + Squad em 2-3 semanas, dependendo da curadoria de metas e segmentação.',
              },
              {
                q: 'Quem opera o produto no dia a dia?',
                a: 'Seu time. O Squad Seraik trabalha junto, mas a operação é sua. Em On-Premise ou Enterprise oferecemos onboarding assistido pela aunica para acelerar a adoção.',
              },
            ].map((item) => (
              <div key={item.q} className="rounded-lg border border-border/30 p-4">
                <p className="font-medium mb-2">{item.q}</p>
                <p className="text-muted-foreground">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal: Formulário */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-2xl border border-border/50 bg-background shadow-2xl p-6">
            {submitted ? (
              <div className="text-center py-8">
                <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-6 w-6 text-green-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Mensagem recebida!</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Um especialista da aunica vai entrar em contato em até 1 dia útil.
                </p>
                <button
                  onClick={() => { setShowForm(false); setSubmitted(false); }}
                  className="rounded-lg bg-muted/30 border border-border/40 px-4 py-2 text-sm hover:bg-muted/50 transition-colors"
                >
                  Fechar
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="font-semibold">Falar com especialista</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">Retorno em até 1 dia útil</p>
                  </div>
                  <button
                    onClick={() => setShowForm(false)}
                    className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:bg-muted/30 transition-colors"
                  >
                    ✕
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Nome *</label>
                      <input
                        required
                        className="w-full rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                        placeholder="Seu nome"
                        value={formData.name}
                        onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Email corporativo *</label>
                      <input
                        required
                        type="email"
                        className="w-full rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                        placeholder="você@empresa.com"
                        value={formData.email}
                        onChange={(e) => setFormData((f) => ({ ...f, email: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Empresa</label>
                      <input
                        className="w-full rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                        placeholder="Nome da empresa"
                        value={formData.company}
                        onChange={(e) => setFormData((f) => ({ ...f, company: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Cargo</label>
                      <input
                        className="w-full rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                        placeholder="Head de Growth, CMO..."
                        value={formData.role}
                        onChange={(e) => setFormData((f) => ({ ...f, role: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Tamanho do time</label>
                      <select
                        className="w-full rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                        value={formData.teamSize}
                        onChange={(e) => setFormData((f) => ({ ...f, teamSize: e.target.value }))}
                      >
                        <option value="">Selecione</option>
                        <option value="1-5">1–5 pessoas</option>
                        <option value="6-20">6–20 pessoas</option>
                        <option value="21-50">21–50 pessoas</option>
                        <option value="50+">Mais de 50</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Trilha de interesse</label>
                      <select
                        className="w-full rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                        value={formData.trackInterest}
                        onChange={(e) => setFormData((f) => ({ ...f, trackInterest: e.target.value }))}
                      >
                        <option value="explorar">Conhecer a plataforma</option>
                        <option value="operacao">Operação para o time</option>
                        <option value="enterprise">Customizado / On-Premise</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">O que você quer resolver?</label>
                    <textarea
                      rows={3}
                      className="w-full rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50 resize-none"
                      placeholder="Conta o contexto: quantos clientes/mesas, fontes principais, o que mais aperta hoje..."
                      value={formData.message}
                      onChange={(e) => setFormData((f) => ({ ...f, message: e.target.value }))}
                    />
                  </div>

                  {error && (
                    <p className="text-xs text-red-400">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-lg bg-foreground text-background py-2.5 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <span className="h-4 w-4 rounded-full border-2 border-background/30 border-t-background animate-spin" />
                    ) : (
                      <>Enviar mensagem <ArrowRight className="h-4 w-4" /></>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
